import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Upload, FileText, Image, File, Trash2, Eye, Download, FileCheck, Edit } from 'lucide-react';
import { DocumentoLegal } from '@/hooks/useDocumentosLegales';
import { useCumplimiento } from '@/hooks/useCumplimiento';
import { useToast } from '@/hooks/use-toast';

interface CumplimientoFormProps {
  documento: DocumentoLegal;
}

const CumplimientoForm = ({ documento }: CumplimientoFormProps) => {
  const { toast } = useToast();
  const { 
    cumplimientos,
    cumplimientoActual,
    evidencias, 
    loading, 
    selectCumplimiento,
    createNewCumplimiento,
    saveCumplimiento, 
    deleteCumplimiento,
    uploadEvidencia, 
    deleteEvidencia 
  } = useCumplimiento(documento.id);

  const [formData, setFormData] = useState({
    acto_administrativo: '',
    articulo: '',
    numeral_literal: '',
    descripcion: ''
  });

  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (cumplimientoActual) {
      setFormData({
        acto_administrativo: cumplimientoActual.acto_administrativo || '',
        articulo: cumplimientoActual.articulo || '',
        numeral_literal: cumplimientoActual.numeral_literal || '',
        descripcion: cumplimientoActual.descripcion || ''
      });
      setShowForm(true);
    } else {
      setFormData({
        acto_administrativo: '',
        articulo: '',
        numeral_literal: '',
        descripcion: ''
      });
    }
  }, [cumplimientoActual]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      const isNewCumplimiento = !cumplimientoActual;
      await saveCumplimiento(formData);
      toast({
        title: "Cumplimiento guardado",
        description: "Los datos de cumplimiento se han guardado correctamente",
      });
      
      // For new cumplimientos, keep the form open so user can upload evidence
      // For existing ones, close the form
      if (!isNewCumplimiento) {
        setShowForm(false);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo guardar el cumplimiento",
        variant: "destructive",
      });
    }
  };

  const handleNewCumplimiento = () => {
    createNewCumplimiento();
    setShowForm(true);
  };

  const handleSelectCumplimiento = (cumplimiento: any) => {
    selectCumplimiento(cumplimiento);
  };

  const handleDeleteCumplimiento = async (cumplimientoId: string) => {
    try {
      await deleteCumplimiento(cumplimientoId);
      toast({
        title: "Cumplimiento eliminado",
        description: "El cumplimiento se ha eliminado correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el cumplimiento",
        variant: "destructive",
      });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      // Validate file types
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp'
      ];

      for (let i = 0; i < files.length; i++) {
        if (!allowedTypes.includes(files[i].type)) {
          toast({
            title: "Formato no permitido",
            description: `El archivo ${files[i].name} no tiene un formato permitido`,
            variant: "destructive",
          });
          return;
        }
      }

      setSelectedFiles(files);
    }
  };

  const handleUploadEvidencias = async () => {
    if (!selectedFiles || !cumplimientoActual) return;

    setUploading(true);
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        await uploadEvidencia(selectedFiles[i], cumplimientoActual.id);
      }
      
      setSelectedFiles(null);
      // Reset file input
      const fileInput = document.getElementById('evidencia-files') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      toast({
        title: "Evidencias subidas",
        description: "Las evidencias se han subido correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron subir algunas evidencias",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (tipoArchivo: string) => {
    if (tipoArchivo.startsWith('image/')) return <Image className="w-4 h-4" />;
    if (tipoArchivo.includes('pdf')) return <FileText className="w-4 h-4 text-red-500" />;
    if (tipoArchivo.includes('word')) return <FileText className="w-4 h-4 text-blue-500" />;
    if (tipoArchivo.includes('excel') || tipoArchivo.includes('sheet')) return <FileText className="w-4 h-4 text-green-500" />;
    if (tipoArchivo.includes('powerpoint') || tipoArchivo.includes('presentation')) return <FileText className="w-4 h-4 text-orange-500" />;
    return <File className="w-4 h-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleViewEvidencia = (evidencia: any) => {
    window.open(evidencia.url_archivo, '_blank');
  };

  const handleDownloadEvidencia = (evidencia: any) => {
    const link = document.createElement('a');
    link.href = evidencia.url_archivo;
    link.download = evidencia.nombre_original;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteEvidencia = async (evidencia: any) => {
    try {
      await deleteEvidencia(evidencia.id);
      toast({
        title: "Evidencia eliminada",
        description: "La evidencia se ha eliminado correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar la evidencia",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Header with New Cumplimiento Button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Cumplimientos</h3>
        <Button onClick={handleNewCumplimiento} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Cumplimiento
        </Button>
      </div>

      {/* Cumplimientos List */}
      {cumplimientos.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-md font-medium text-muted-foreground">Cumplimientos Existentes</h4>
          <div className="grid gap-3">
            {cumplimientos.map((cumplimiento) => (
              <Card key={cumplimiento.id} className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0" onClick={() => handleSelectCumplimiento(cumplimiento)}>
                      <div className="flex items-center gap-2 mb-2">
                        <FileCheck className="w-4 h-4 text-blue-600" />
                        <span className="font-medium text-sm">
                          {cumplimiento.acto_administrativo || 'Sin acto administrativo'}
                        </span>
                      </div>
                      
                      <div className="space-y-1 text-xs text-muted-foreground">
                        {cumplimiento.articulo && (
                          <p><span className="font-medium">Artículo:</span> {cumplimiento.articulo}</p>
                        )}
                        {cumplimiento.numeral_literal && (
                          <p><span className="font-medium">Numeral/Literal:</span> {cumplimiento.numeral_literal}</p>
                        )}
                        {cumplimiento.descripcion && (
                          <p className="truncate"><span className="font-medium">Descripción:</span> {cumplimiento.descripcion}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Creado: {new Date(cumplimiento.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-1 ml-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSelectCumplimiento(cumplimiento)}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar cumplimiento?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta acción no se puede deshacer. Se eliminará el cumplimiento y todas sus evidencias.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteCumplimiento(cumplimiento.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-md font-medium">
              {cumplimientoActual ? 'Editar Cumplimiento' : 'Nuevo Cumplimiento'}
            </h4>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setShowForm(false);
                createNewCumplimiento();
              }}
            >
              Cancelar
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="acto-administrativo">ACTO ADMINISTRATIVO</Label>
              <Input
                id="acto-administrativo"
                value={formData.acto_administrativo}
                onChange={(e) => handleInputChange('acto_administrativo', e.target.value)}
                placeholder="Ingrese el acto administrativo"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="articulo">ARTÍCULO</Label>
              <Input
                id="articulo"
                value={formData.articulo}
                onChange={(e) => handleInputChange('articulo', e.target.value)}
                placeholder="Ingrese el artículo"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="numeral-literal">NUMERAL / LITERAL</Label>
              <Input
                id="numeral-literal"
                value={formData.numeral_literal}
                onChange={(e) => handleInputChange('numeral_literal', e.target.value)}
                placeholder="Ingrese el numeral o literal"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="descripcion">DESCRIPCIÓN</Label>
            <Textarea
              id="descripcion"
              value={formData.descripcion}
              onChange={(e) => handleInputChange('descripcion', e.target.value)}
              placeholder="Ingrese la descripción del cumplimiento"
              rows={4}
            />
          </div>

          {/* Save Button immediately after form fields */}
          <div className="pt-4">
            <Button onClick={handleSave} className="w-full" size="lg">
              {cumplimientoActual ? 'Actualizar Cumplimiento' : 'Guardar Cumplimiento'}
            </Button>
          </div>
        </div>
      )}

      {/* Evidencias Section - show when cumplimiento exists (after saving) */}
      {cumplimientoActual && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              EVIDENCIA
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Formatos permitidos: PDF, Word, Excel, PowerPoint e Imágenes
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File Upload */}
            <div className="border-2 border-dashed border-muted rounded-lg p-4">
              <div className="flex flex-col items-center space-y-2">
                <Upload className="w-8 h-8 text-muted-foreground" />
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    Selecciona archivos para subir como evidencia
                  </p>
                  <input
                    id="evidencia-files"
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('evidencia-files')?.click()}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Seleccionar Archivos
                  </Button>
                </div>
              </div>
            </div>

            {/* Selected Files Preview */}
            {selectedFiles && selectedFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Archivos seleccionados:</p>
                <div className="space-y-1">
                  {Array.from(selectedFiles).map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                      <div className="flex items-center gap-2">
                        {getFileIcon(file.type)}
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-muted-foreground">({formatFileSize(file.size)})</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                <Button
                  onClick={handleUploadEvidencias}
                  disabled={uploading}
                  className="w-full"
                >
                  {uploading ? 'Subiendo...' : 'Subir Evidencias'}
                </Button>
              </div>
            )}

            {/* Evidencias List */}
            {evidencias.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Evidencias subidas:</p>
                <div className="grid gap-2">
                  {evidencias.map((evidencia) => (
                    <div key={evidencia.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {getFileIcon(evidencia.tipo_archivo)}
                        <div>
                          <p className="text-sm font-medium">{evidencia.nombre_original}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(evidencia.tamano_archivo)} • {new Date(evidencia.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewEvidencia(evidencia)}
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadEvidencia(evidencia)}
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar evidencia?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción no se puede deshacer. La evidencia "{evidencia.nombre_original}" será eliminada permanentemente.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteEvidencia(evidencia)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

    </div>
  );
};

export default CumplimientoForm;